import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Sparkles, ArrowUpCircle } from "lucide-react";

/**
 * Renders a friendly upgrade dialog when an API call returns a 402 with
 * code=PLAN_LIMIT_REACHED. The parent should manage the `open` state and
 * pass the parsed `detail` payload.
 */
export function PlanLimitDialog({ open, onOpenChange, detail }) {
  const navigate = useNavigate();
  if (!detail) return null;

  const {
    resource = "items",
    current_count = 0,
    limit = 0,
    current_plan = "business",
    suggested_upgrade = "enterprise",
    message,
  } = detail;

  const pct = limit > 0 ? Math.min(100, Math.round((current_count / limit) * 100)) : 100;
  const planLabel = (p) => p.charAt(0).toUpperCase() + p.slice(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="plan-limit-dialog">
        <DialogHeader>
          <div className="mx-auto mb-3 p-4 rounded-full bg-amber-500/10 w-fit">
            <ArrowUpCircle className="h-8 w-8 text-amber-500" />
          </div>
          <DialogTitle className="text-center text-xl">You've reached your {planLabel(current_plan)} plan limit</DialogTitle>
          <DialogDescription className="text-center">
            {message || `You've used all ${limit} ${resource} included with your ${planLabel(current_plan)} plan.`}
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground capitalize">{resource} used</span>
            <span className="font-medium">{current_count} / {limit}</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>

        <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/0 p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <p className="font-semibold text-sm">Upgrade to {planLabel(suggested_upgrade)}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            {suggested_upgrade === "enterprise"
              ? "Get up to 25 employees, 500 clients, full payroll, automated workflows, and LIVE processing add-ons."
              : "Unlimited employees, clients, multi-entity consolidation, and dedicated support."}
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="plan-limit-cancel-btn">
            Maybe later
          </Button>
          <Button
            className="flex-1"
            onClick={() => { onOpenChange(false); navigate("/manage-subscription"); }}
            data-testid="plan-limit-upgrade-btn"
          >
            Upgrade now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
